import { apiFetch } from '@/lib/api';
import type { AdminNotification } from '@/lib/notifications';
import type { SopStepRow } from '@/lib/sops-admin';
import { buildSopFlowHealth, type SopFlowHealthIssue } from '@/lib/sops-workflow-author';

/** Notification types whose fix surface is SOP workflow structure. */
export const ADMIN_NOTIFICATION_SOP_FLOW_TYPES = new Set([
  'sop_incomplete_flow',
  'scenario_invalid_sop_flow',
  'sop_no_end_step',
  'sop_no_submit_terminal',
  'step_missing_contact_reference',
  'step_scoped_asset_ref_invalid',
]);

export function isAdminNotificationSopFlowType(type: string): boolean {
  return ADMIN_NOTIFICATION_SOP_FLOW_TYPES.has(type);
}

export function resolveAdminNotificationSopId(
  row: Pick<AdminNotification, 'sopId' | 'entityType' | 'entityId'>,
): number | null {
  const fromColumn = Number(row.sopId);
  if (Number.isFinite(fromColumn) && fromColumn > 0) return fromColumn;
  if (row.entityType === 'sop') {
    const entityId = Number(row.entityId);
    if (Number.isFinite(entityId) && entityId > 0) return entityId;
  }
  return null;
}

export async function fetchSopStructuralFlowIssues(sopId: number): Promise<SopFlowHealthIssue[]> {
  const res = await apiFetch<{ ok?: boolean; data?: SopStepRow[] }>(`/api/sops/${sopId}/steps`);
  const steps = res.data ?? [];
  return buildSopFlowHealth(steps).issues;
}

const flowIssuesCache = new Map<number, SopFlowHealthIssue[]>();
const flowIssuesInFlight = new Map<number, Promise<SopFlowHealthIssue[]>>();

let fetchOverrideForTests: typeof fetchSopStructuralFlowIssues | null = null;

function resolveFlowIssuesFetch(): typeof fetchSopStructuralFlowIssues {
  return fetchOverrideForTests ?? fetchSopStructuralFlowIssues;
}

/** Deduped in-flight fetch; caches successful responses only. */
export function loadCachedSopStructuralFlowIssues(sopId: number): Promise<SopFlowHealthIssue[]> {
  const cached = flowIssuesCache.get(sopId);
  if (cached) {
    return Promise.resolve(cached);
  }

  const inFlight = flowIssuesInFlight.get(sopId);
  if (inFlight) {
    return inFlight;
  }

  const promise = resolveFlowIssuesFetch()(sopId)
    .then((issues) => {
      flowIssuesCache.set(sopId, issues);
      return issues;
    })
    .finally(() => {
      if (flowIssuesInFlight.get(sopId) === promise) {
        flowIssuesInFlight.delete(sopId);
      }
    });

  flowIssuesInFlight.set(sopId, promise);
  return promise;
}

/** Test helper — clears module cache and optional fetch override between unit tests. */
export function resetAdminNotificationFlowIssuesCacheForTests(): void {
  flowIssuesCache.clear();
  flowIssuesInFlight.clear();
  fetchOverrideForTests = null;
}

/** Test helper — inject fetch behavior for cache/retry unit tests. */
export function configureAdminNotificationFlowIssuesForTests(options?: {
  fetch?: typeof fetchSopStructuralFlowIssues;
}): void {
  resetAdminNotificationFlowIssuesCacheForTests();
  fetchOverrideForTests = options?.fetch ?? null;
}
